import { useState } from 'react';
import { Link } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useDecideResearchUses, useFindResearchUses, useResearchUses, type WireResearchUse } from '@/api/hooks';
import { NewTask, TwinHandoff } from '@/tasks/Assign';
import { Explainer } from '@/ui/context';
import { Select } from '@/ui/fields';
import { Badge, Button, Kicker } from '@/ui/primitives';
import { useToast } from '@/ui/Toast';
import { pageLabel, type ParsedResearch } from './ResearchView';
import { USE_GROUPS, explainUse, groupTask, nextSteps, outcomeLink, useTask, type NextStepSuggestion } from './research-next';

/**
 * What to do with a research answer, for the department that asked
 * (ADR-0175): the next steps the answer points to, each a task for whoever
 * the person picks, and what the answer is good for, found by the
 * department's agent on a press and taken by the lead.
 */

type Draft = { title: string; body: string } | null;

export function ResearchNext({ parsed, requestId }: { parsed: ParsedResearch; requestId: string }) {
  const steps = nextSteps(parsed, requestId);
  const [draft, setDraft] = useState<Draft>(null);
  if (steps.length === 0) return null;
  return (
    <section className="flex flex-col gap-3" aria-label="What to do next" data-testid="research-next">
      <Kicker>What to do next</Kicker>
      <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 @lg:grid-cols-2">
        {steps.map((s) => (
          <StepCard key={s.key} step={s} onTask={() => setDraft({ title: s.title, body: s.body })} />
        ))}
      </ul>
      {draft && <NewTask onClose={() => setDraft(null)} initialTitle={draft.title} initialBody={draft.body} stay />}
    </section>
  );
}

function StepCard({ step, onTask }: { step: NextStepSuggestion; onTask: () => void }) {
  return (
    <li className="flex flex-col gap-2 border border-line p-3" data-testid="research-step">
      <span className="text-dense font-semibold">{step.label}</span>
      <p className="m-0 text-dense text-secondary">{step.why}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="row" onClick={onTask}>Make it a task</Button>
        {step.to && (
          <Link to={step.to.path} className="text-dense text-accent underline-offset-2 hover:underline">
            {step.to.label}
          </Link>
        )}
      </div>
      <TwinHandoff title={step.title} />
    </li>
  );
}

const ORDER: WireResearchUse['kind'][] = ['prospect', 'competitor', 'supplier', 'price', 'check'];

export function ResearchUses({ requestId, findable = true }: { requestId: string; findable?: boolean }) {
  const uses = useResearchUses(requestId, true);
  const find = useFindResearchUses();
  const decide = useDecideResearchUses();
  const toast = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [planId, setPlanId] = useState('');
  const [draft, setDraft] = useState<Draft>(null);

  const data = uses.data;
  const all = data?.uses ?? [];
  const pending = data?.pending === true || find.isPending;
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const run = (take: string[], decline: string[]) =>
    decide.mutate(
      { requestId, take, decline, ...(planId ? { planId } : {}) },
      {
        onSuccess: (r) => {
          setPicked(new Set());
          const parts = [r.taken.length ? `${r.taken.length} taken` : '', r.declined ? `${r.declined} declined` : '', r.failed.length ? `${r.failed.length} could not be taken; each says why` : ''].filter(Boolean);
          toast({ text: `${parts.join(', ')}.` });
        },
        onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
      },
    );
  const pickedProspects = all.filter((u) => picked.has(u.id) && u.kind === 'prospect').length;

  return (
    <section className="flex flex-col gap-3 rounded-brand border border-line p-4" aria-label="What this research is good for" data-testid="research-uses">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Kicker>What this research is good for</Kicker>
        {findable && <Button
          size="row"
          variant={all.length === 0 ? 'primary' : 'secondary'}
          disabled={pending}
          onClick={() => find.mutate({ requestId }, { onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}
        >
          {pending ? 'Reading the answer…' : all.length === 0 ? 'Find the uses' : 'Look again'}
        </Button>}
      </div>
      {!findable && all.length === 0 && !pending && <p className="m-0 text-dense text-secondary">Each company found is filed here when the answer is recorded, usually within the hour.</p>}
      {findable && all.length === 0 && !pending && (
        <p className="m-0 text-dense text-secondary">
          The department’s agent reads the findings and proposes what they name: businesses that could buy, competitors, makers, prices for the market’s page, and findings to read back. Each keeps the words it rests on; your lead takes or declines it, and anyone can give it out as a task.
        </p>
      )}
      {pending && <p className="m-0 text-dense text-secondary">The agent is reading the answer. This takes under a minute.</p>}
      {!pending && all.length === 0 && data?.lastError && <p className="m-0 text-dense text-danger">The last look found nothing: {data.lastError}</p>}

      {ORDER.map((kind) => {
        const group = all.filter((u) => u.kind === kind);
        if (group.length === 0) return null;
        const open = group.filter((u) => u.state === 'proposed');
        return (
          <div key={kind} className="flex flex-col gap-2" data-testid={`uses-${kind}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="m-0 text-dense font-semibold">
                {USE_GROUPS[kind].heading} <span className="text-meta">· {group.length}</span>
              </h3>
              {open.length > 1 && (
                <Button size="row" onClick={() => setDraft(groupTask(kind, open, requestId))}>
                  Give all {open.length} as one task
                </Button>
              )}
            </div>
            <p className="m-0 text-micro text-meta">{USE_GROUPS[kind].takes}</p>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {group.map((u) => (
                <UseRow key={u.id} use={u} requestId={requestId} mayTake={data?.mayTake === true} picked={picked.has(u.id)} onPick={() => toggle(u.id)} onTask={() => setDraft(useTask(u, requestId))} />
              ))}
            </ul>
          </div>
        );
      })}

      {data?.mayTake && picked.size > 0 && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-line bg-surface pt-3" data-testid="uses-decide">
          <span className="text-dense">{picked.size} chosen</span>
          {pickedProspects > 0 && (data.plans.length > 1 ? (
            <Select aria-label="The plan they are found under" value={planId} onChange={(e) => setPlanId(e.target.value)} options={[{ value: '', label: 'Under which plan?' }, ...data.plans.map((p) => ({ value: p.id, label: p.name }))]} />
          ) : data.plans.length === 0 ? (
            <span className="text-dense text-warning">No plan runs in this market, so prospects cannot be taken yet; give them out as a task.</span>
          ) : null)}
          <Button size="row" variant="primary" disabled={decide.isPending} onClick={() => run([...picked], [])}>Take</Button>
          <Button size="row" disabled={decide.isPending} onClick={() => run([], [...picked])}>Decline</Button>
        </div>
      )}
      {draft && <NewTask onClose={() => setDraft(null)} initialTitle={draft.title} initialBody={draft.body} stay />}
    </section>
  );
}

const STATE_TONE: Record<WireResearchUse['state'], 'neutral' | 'accent' | 'warning' | 'danger'> = { proposed: 'neutral', taken: 'accent', declined: 'neutral', failed: 'warning' };
const STATE_WORDS: Record<WireResearchUse['state'], string> = { proposed: 'Proposed', taken: 'Taken', declined: 'Declined', failed: 'Not taken' };

function UseRow({ use: u, requestId, mayTake, picked, onPick, onTask }: { use: WireResearchUse; requestId: string; mayTake: boolean; picked: boolean; onPick: () => void; onTask: () => void }) {
  const d = u.detail;
  const link = outcomeLink(u);
  return (
    <li className="flex flex-col gap-1.5 border-l-2 border-line pl-3" data-testid="research-use">
      <div className="flex flex-wrap items-center gap-2">
        {mayTake && u.state === 'proposed' && <input type="checkbox" aria-label={`Choose ${d.name}`} checked={picked} onChange={onPick} className="size-4" />}
        <span className="text-dense font-semibold">{d.name}</span>
        {d.role && <Badge plain>{d.role}</Badge>}
        <Badge tone={STATE_TONE[u.state]}>{STATE_WORDS[u.state]}</Badge>
        {d.price && (
          <span className="text-dense">
            {d.price.amount} {d.price.currency} per {d.price.unit} · {d.price.product}
          </span>
        )}
      </div>
      {d.quote ? <p className="m-0 text-dense text-secondary">“{d.quote}”</p> : d.finding_text ? <p className="m-0 text-dense text-secondary">{d.finding_text}</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        {d.url && (
          <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-dense text-accent underline-offset-2 hover:underline [overflow-wrap:anywhere]">
            {pageLabel(d.url)}
          </a>
        )}
        {link && (
          <Link to={link.to} className="text-dense text-accent underline-offset-2 hover:underline">
            {link.label}
          </Link>
        )}
        {u.state === 'failed' && u.note && <span className="text-dense text-warning">{u.note}</span>}
        {u.state === 'proposed' && (
          <Button size="row" onClick={onTask}>
            Give as a task
          </Button>
        )}
      </div>
      <Explainer context={explainUse(u, { mayTake, requestId })} label="Why this" />
    </li>
  );
}
